/** @module csi/session */
define([
	"dojo/_base/array",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/comm",
	"csi/reflection",
	"csi/resolver",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/routerUtility",
	"csi/utility/coreUtility"
], function(array, csi, _, crews, comm, reflection, resolver, sessionConstants, state, {routerStore, sessionStore}, routerUtility, {isNullOrUndefined}) {

	// initialize cache for resolver from here to reduce module (circular) dependencies
	resolver.initialize(sessionStore.nonObservableCache);

	// TODO: move more location properties/functions to csi/state/router
	const {
		LOCATION_COMPARE_URL,
		LOCATION_DISABLE_TAB_MATCHING,
		LOCATION_REVISION_URL,
		LOCATION_TAB,
		LOCATION_URL,
		captureHash,
		decomposeHash,
		getLocationHash,
		replaceHash,
		setLocationHash,
	} = routerUtility;

	// LocationItem[]: array of location keys{base,key,function} triples currently reserved for use in document location
	let _locationObjects = [];

	return {
		LOCATION_COMPARE_URL,
		LOCATION_CUSTOM_VIEW: "CustomView",
		LOCATION_FILMSTRIP: "Filmstrip",
		LOCATION_FONT_TIMEOUT: "FontTimeout",
		LOCATION_HTML_FILE: "Html",
		LOCATION_LOCALE: "Locale",
		LOCATION_ORIENTATION: "Orientation",
		LOCATION_PAPER: "Paper",
		LOCATION_REVISION_URL,
		LOCATION_TAB,
		LOCATION_URL,
		LOCATION_VIEW: "PrintView",


		// String: previous hash location
		_hashLast: null,

		// Object: keys from document location (may not yet be loaded)
		_location: {},

		// Object: keys from currently loaded document location
		_locationActuals: {},

		captureLocation: function() {
			// summary: update hashtables with new hash key/value pairs; return true only if new location
			const hash = captureHash();

			// do nothing if hash is same as before
			if (routerStore.locationUrl && (hash == this._hashLast)) {
				return false;
			}

			// set location tokens from new hash - if parameter included more than once, use last one
			this._location = decomposeHash(hash);

			this._hashLast = hash;
			return true;
		},

		releaseLocationKey: function(/*String*/key) {
			// summary: free up location key for use
			_locationObjects.dele(function(obj) {return obj.key == key;});
			delete this._locationActuals[key];
		},

		/**
		 * Reserve key for use in location; callback function is used to notify of change to location key
		 * @param {string} baseKey
		 * @param {LocationCallback} callback
		 * @return {string} return actual location key which could be numbered version of given key
		 */
		reserveLocationKey: function(baseKey, callback) {
			let n = 0;

			for (const obj of _locationObjects) {
				const {key} = obj;

				if (key === baseKey) {
					++n;
				} else if (key.startsWith(baseKey)) {
					// if only an integer after the baseKey, then increment counter for new key
					const a = key.split(baseKey);

					if (!isNaN(a[1])) {
						++n;
					}
				}
			}

			const key = baseKey + (n ? String(n) : "");
			_locationObjects.push({key, callback});
			return key;
		},

		/**
		 * Update current value for location key - filter out all keys after changed key and return
		 * them as a history of the previous state
		 * @param {string} key
		 * @param {string} value
		 * @param {LocationItemState[]} actualsAdd
		 * @param {LocationCallback} callback
		 * @param {boolean} keepAll
		 * @return {LocationItemState[]}
		 */
		setLocationActual: function(key, value, actualsAdd, callback, keepAll) {
			var s = this;
			var actualsOld = [];
			var objLocation;

			function setActual(k, v) {
				// set new actuals for key
				s._locationActuals[k] = v || "";
				s._location[k] = v;
			}

			_locationObjects = _locationObjects.filter((obj) => {
				if (objLocation && !keepAll) {
					// merge all information about previous key for history
					const actual = this._locationActuals[obj.key];
					if (actual) {
						// location objects don't normally have a value, but this is needed for return value
						// which then comes back into this function through actualsAdd (which requires the value)
						obj.value = actual;
					}

					actualsOld.push(obj);
					return false;
				}

				if (key == obj.key) {
					// replace current notification for key with new function
					obj.callback = callback;
					objLocation = obj;
				}

				return true;
			});

			// if key is no longer reserved because a prior key was set, then add again -
			// this can happen when refreshing view rather than creating it from scratch
			if (!objLocation) {
				objLocation = {key, callback};
				_locationObjects.push(objLocation);
			}

			setActual(key, value);

			// add additional actuals back if any - this mirrors old actuals above
			array.forEach(actualsAdd, function(obj) {
				_locationObjects.push(obj);
				setActual(obj.key, obj.value);
			}, this);

			// replace history with actual location
			this._hashLast = this.getLocation(null, this._location);
			replaceHash(this._hashLast);

			// update router store with latest hash parameters
			routerStore.captureLocation();

			return actualsOld;
		},

		setLocationKey: function(/*String*/key, /*String*/value, /*Boolean*/keepAll) {
			// summary: update location with new value for key if different than previous value
			if (keepAll && (this.getLocationValue(key) == value)) {
				return;
			}

			// if not keeping all parameters, then prune location at given key
			var params = this.getLocationParams(keepAll ? null : key);
			params[key] = value;
			setLocationHash(this.getLocation(null, params));
		},

		setLocationUrl: function(/*String*/url, /*Boolean*/bChangedData, /*Boolean*/disableTabMatching) {
			// summary: disable query accelerator temporarily when navigating to a new node if changes were just made to that node
			if (bChangedData) {
				comm.bypassQueryAccelerator();
			}

			setLocationHash(this.getLocation(null, {
				[LOCATION_URL]: url,
				[LOCATION_DISABLE_TAB_MATCHING]: disableTabMatching ? "1" : null,
			}));
		},

		/**
		 * Return location for given page and params, prepending htmlPage unless wanting only hash
		 * @param {string} [htmlPage]
		 * @param {Object} [hashParams]
		 * @param {boolean} [bWithDomain]
		 * @return {string}
		 */
		getLocation: function(htmlPage, hashParams, bWithDomain) {
			const locationHash = getLocationHash(hashParams || this._location);
			return htmlPage ? csi.url(`${htmlPage}.html${locationHash}`, bWithDomain) : locationHash;
		},

		getLocationActual: function(/*String*/key) {
			return this._locationActuals[key];
		},

		getLocationDefault: function(/*String?*/urlPrevious) {
			// summary: determine default location for current user given the previous location (that may have failed);
			// if the user is local then its only default location is its crew's parent; otherwise default to Setup or Home depending on user permissions
			// Admin users playing a role will redirect to Home
			var url = sessionConstants.userCompanyUrl || (crews.isAdmin() && !sessionConstants.roleUrl ? reflection.URL_SITE : reflection.URL_HOME);

			if (url == urlPrevious) {
				url = null;
			}

			// do this so that existing tabs are not used for default location
			this._locationActuals = {};

			return url;
		},

		getLocationForUrl: function(/*String*/url, /*Object?*/params,/*boolean?*/bWithDomain) {
			// summary: return location string for url with parameters and domain if configured
			params = params || {};
			params[LOCATION_URL] = url || params[LOCATION_URL];
			return this.getLocation("home", params, bWithDomain);
		},

		getLocationKeyChanged: function() {
			// summary: return location key whose value changed as part of new document location
			var bPrint = state.get("print");
			var key;

			array.some(_locationObjects, function(obj, i) {
				// compare new location tokens against actuals to find first key where they differ -
				// always refresh full page when printing
				let v1 = bPrint ? null : this.getLocationActual(obj.key);
				let v2 = this.getLocationValue(obj.key);

				// if new value is not specified, then assume value is same as before and skip
				if (!isNullOrUndefined(v1) && isNullOrUndefined(v2)) {
					return false;
				}

				v1 = v1 || "";
				v2 = v2 || "";

				if ((v2 != v1) || (!i && !v1)) {
					key = obj.key;
					return true;
				}

				return false;
			}, this);

			return key;
		},

		/**
		 * Return callback function for given location key
		 * @param {string} key
		 * @return {LocationCallback|null}
		 */
		getLocationKeyFunction: function(key) {
			const obj = _locationObjects.find(obj => obj.key === key);
			return obj ? obj.callback : null;
		},

		getLocationParams: function(/*String?*/keyPrune) {
			// summary: return object containing current location parameters; if key is passed, then
			// only provide parameters for keys appearing before that key in location
			// NOTE: when printing, there are many parameters not reserved in _locationObjects, so just
			// return all parameters in hash - not sure if this will pose problems or not (changed to
			// allow for BOM toolbar plugins to change URL without causing location change)
			if (state.get("print")) {
				return decomposeHash();
			}

			var params = {};

			array.some(_locationObjects, function(obj) {
				if (obj.key == keyPrune) {
					return true;
				}

				var v = this.getLocationActual(obj.key);
				if (v != null) {
					params[obj.key] = v;
				}

				return false;
			}, this);

			return params;
		},

		getLocationValue: function(/*String*/key) {
			// summary: return value for given key
			return this._location[key];
		},

		restoreActualLocation: function() {
			// summary: update browser hash with actual (existing) location rather than that indicated by browser URL
			this._location = this.getLocationParams();
			this._hashLast = this.getLocation();
			replaceHash(this._hashLast);
		}
	};
});
